import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { first } from 'lodash';
import { useSnackbar } from 'notistack';
import type { FunctionComponent } from 'react';
import React from 'react';
import { useStore } from 'zustand';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuItemIndicator,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuRadioItemContent,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import {
    IconArchive,
    IconAsterisk,
    IconCheckmark1Small,
    IconDocument,
    IconExternalLink,
    IconPeopleAdd,
    IconSpacer,
    IconTable,
} from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';
import type { VersionSelect } from '~/ui/domain/version-select/VersionSelect';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { InviteTeamMemberDialog } from '../../company-settings/tabs/organization-tab/users-and-roles/InviteTeamMemberDialog';
import { JOB_ARCHITECTURE_INVITATION_ROLES, useInviteTeamMember } from '../api/use-invite-team-member';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { usePdfExport } from '../api/use-pdf-export';
import { useUnpublishJobItemVersion } from '../api/use-unpublish-job-item-version';
import { GenericReviewerContextMenuComponent } from '../components/ReviewerComponents';
import { WatchMenuItem } from '../components/WatchMenuItem';
import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { getProfilePath, parseProfileVersion } from '../url-navigation/use-navigate-to-standalone-profile';
import { useDraftVersionStore } from './DraftVersionStoreProvider';

const StyledDropdownMenuContent = styled(DropdownMenuContent)({
    zIndex: 3000,
});

const MenuItemWithIcon = styled.span({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
});

const VersionBadge = styled.span({
    display: 'inline-block',
    transform: 'scale(0.8)',
});

type ExportPdfMenuItemProps = {
    readonly item: JobItemDetails;
    readonly version: number;
};

const ExportPdfMenuItem: FunctionComponent<ExportPdfMenuItemProps> = ({ item, version }) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const { exportPdf, isLoading } = usePdfExport({
        jobArchitectureId: item.architectureId,
        jobArchitectureItemId: item.id,
        jobArchitectureItemType: item.type,
        languageCode: activeLanguage,
        version,
    });

    const handleSelect = (event: Event): void => {
        if (isLoading) {
            event.preventDefault();

            return;
        }
        event.preventDefault();
        exportPdf();
    };

    return (
        <DropdownMenuItem disabled={isLoading} onSelect={handleSelect}>
            {isLoading ? <Spinner style={{ color: '#DADAD8' }} /> : <IconDocument size={16} />}
            {translate('pages.job-architecture.export-pdf-button')}
        </DropdownMenuItem>
    );
};

type OpenStandaloneMenuItemProps = {
    readonly onOpenSeparatePage: () => void;
};

const ShareJobMenuItem: FunctionComponent<{
    readonly item: JobItemDetails;
    readonly selectedVersion: string | undefined;
}> = ({ item, selectedVersion }) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const { enqueueSnackbar } = useSnackbar();

    return (
        <DropdownMenuItem
            onSelect={() => {
                const url =
                    window.location.origin +
                    getProfilePath({
                        architectureId: item.architectureId,
                        itemId: item.id,
                        itemType: item.type,
                        preferredLanguage: activeLanguage,
                        version: parseProfileVersion(selectedVersion),
                    });
                navigator.clipboard.writeText(url);
                enqueueSnackbar(translate('pages.job-architecture-v2.job-profile-copied-to-clipboard'), {
                    variant: 'success',
                });
            }}
        >
            <IconExternalLink size={16} />
            {translate('common.share')}
        </DropdownMenuItem>
    );
};

const OpenStandaloneMenuItem: FunctionComponent<OpenStandaloneMenuItemProps> = ({ onOpenSeparatePage }) => {
    const translate = useTranslate();

    return (
        <DropdownMenuItem onSelect={onOpenSeparatePage}>
            <MenuItemWithIcon>
                <IconSpacer />
                {translate('pages.job-architecture-v2.open-separate-page')}
            </MenuItemWithIcon>
        </DropdownMenuItem>
    );
};

type VersionInfo = {
    readonly version: number;
};

const getDraftCompareVersions = (
    selectedVersion: string,
    versions: readonly VersionInfo[],
): readonly [string, string] => {
    const latestVersionNumber = first(versions)?.version ?? 0;

    return [selectedVersion, (latestVersionNumber + 1).toString()];
};

const getExistingCompareVersions = (
    selectedVersion: string,
    versions: readonly VersionInfo[],
): readonly [string, string] | undefined => {
    if (versions.length < 2) {
        return undefined;
    }

    const latestVersion = first(versions);
    if (is.nullOrUndefined(latestVersion)) {
        return undefined;
    }

    const versionToCompare = selectedVersion === latestVersion.version.toString() ? versions[1] : latestVersion;
    if (is.nullOrUndefined(versionToCompare)) {
        return undefined;
    }

    // biome-ignore lint/correctness/useParseIntRadix: suppressed due to migration
    const versionA = Math.min(versionToCompare.version, Number.parseInt(selectedVersion));
    // biome-ignore lint/correctness/useParseIntRadix: suppressed due to migration
    const versionB = Math.max(versionToCompare.version, Number.parseInt(selectedVersion));

    return [versionA.toString(), versionB.toString()];
};

const getCompareVersionPair = (
    selectedVersion: string,
    versions: readonly VersionInfo[],
    hasDraft: boolean,
): readonly [string, string] | undefined => {
    if (hasDraft) {
        return getDraftCompareVersions(selectedVersion, versions);
    }

    return getExistingCompareVersions(selectedVersion, versions);
};

type VersionSubMenuProps = {
    readonly currentVersionId: string | undefined;
    readonly formattedVersions: readonly VersionSelect[];
    readonly isEditing: boolean;
    readonly onVersionChange: (value: string) => void;
};

const VersionSubMenu: FunctionComponent<VersionSubMenuProps> = ({
    currentVersionId,
    formattedVersions,
    isEditing,
    onVersionChange,
}) => {
    const translate = useTranslate();

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={isEditing}>
                <MenuItemWithIcon>
                    <IconSpacer />
                    {translate('pages.job-architecture-v2.job-profile-version-placeholder')}
                </MenuItemWithIcon>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
                style={{
                    zIndex: 3000,
                    maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
                    overflowY: 'auto',
                }}
            >
                <DropdownMenuRadioGroup value={currentVersionId} onValueChange={(value) => onVersionChange(value)}>
                    {formattedVersions.map((version) => (
                        <DropdownMenuRadioItem key={version.id} value={version.id}>
                            <DropdownMenuRadioItemContent>
                                {version.title}
                                <VersionBadge>{version.badge}</VersionBadge>
                            </DropdownMenuRadioItemContent>
                            <DropdownMenuItemIndicator>
                                <IconCheckmark1Small size={16} />
                            </DropdownMenuItemIndicator>
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
};

type ReviewerSubMenuProps = {
    readonly canAssignReviewer: boolean;
    readonly item: JobItemDetails;
    readonly onInvite?: () => void;
};

const ReviewerSubMenu: FunctionComponent<ReviewerSubMenuProps> = ({ canAssignReviewer, item, onInvite }) => {
    const translate = useTranslate();

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={!canAssignReviewer}>
                <MenuItemWithIcon>
                    <IconPeopleAdd size={16} />
                    {translate('pages.job-architecture-v2.job-assign-reviewer')}
                </MenuItemWithIcon>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent style={{ minHeight: '100px', zIndex: 3000 }}>
                <GenericReviewerContextMenuComponent
                    item={item}
                    itemType={item.type}
                    jobArchitectureId={item.architectureId}
                    jobClusterId={item.parentId}
                    onlyAssignReviewer={true}
                    onInvite={onInvite}
                />
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
};

const UnpublishOption: FunctionComponent<{ readonly item: JobItemDetails }> = ({ item }) => {
    const translate = useTranslate();
    const { confirm } = useConfirm();
    const { showErrorSnackbar } = useSnackbars();
    const { mutateAsync: unpublishVersion } = useUnpublishJobItemVersion();

    if (item.versionInfo?.isPublished !== true) {
        return null;
    }
    const version = item.versionInfo.version.toString();

    const handleUnpublish = async (): Promise<void> => {
        try {
            await confirm({
                confirmButtonLabel: translate('pages.job-architecture-v2.unpublish'),
                description: translate('pages.job-architecture-v2.unpublish-description'),
                title: translate('pages.job-architecture-v2.unpublish-title'),
            });
        } catch {
            return;
        }

        try {
            await unpublishVersion({
                architectureId: item.architectureId,
                itemId: item.id,
                itemType: item.type,
                version,
            });
        } catch (error) {
            showErrorSnackbar(error);
        }
    };

    return (
        <DropdownMenuItem disabled={item.actions?.canPublish !== true} onSelect={() => void handleUnpublish()}>
            <MenuItemWithIcon>
                <IconSpacer />
                {translate('pages.job-architecture-v2.unpublish')}
            </MenuItemWithIcon>
        </DropdownMenuItem>
    );
};

const isValidVersionForCompare = (version: string | undefined): version is string => {
    return !is.nullOrUndefined(version) && !is.emptyString(version);
};

const getCurrentVersionId = (
    isEditing: boolean,
    newTmpVersion: VersionSelect,
    formattedVersions: readonly VersionSelect[],
    selectedVersion: string | undefined,
): string | undefined => {
    if (isEditing) {
        return newTmpVersion.id;
    }

    return formattedVersions.find((v) => v.id === selectedVersion)?.id;
};

type ActionMenuItemsProps = {
    readonly compareDisabledTooltip?: string;
    readonly isCompareDisabled: boolean;
    readonly isEditingDisabled: boolean;
    readonly isRegenerateDisabled: boolean;
    readonly onCompareVersions: () => void;
    readonly onCompareLevels?: () => void;
    readonly onCompareRoles?: () => void;
    readonly onNewVersion: () => void;
    readonly onRegenerate: () => void;
};

const ActionMenuItems: FunctionComponent<ActionMenuItemsProps> = ({
    compareDisabledTooltip,
    isCompareDisabled,
    isEditingDisabled,
    isRegenerateDisabled,
    onCompareVersions,
    onCompareLevels,
    onCompareRoles,
    onNewVersion,
    onRegenerate,
}) => {
    const translate = useTranslate();

    return (
        <React.Fragment>
            <DropdownMenuItem disabled={isEditingDisabled} onSelect={onNewVersion}>
                <MenuItemWithIcon>
                    <IconSpacer />
                    {translate('pages.job-architecture-v2.job-profile-edit-manually')}
                </MenuItemWithIcon>
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isRegenerateDisabled} onSelect={onRegenerate}>
                <MenuItemWithIcon>
                    <IconAsterisk size={16} />
                    {translate('pages.job-architecture-v2.job-profile-edit-with-ai')}
                </MenuItemWithIcon>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
                disabled={isCompareDisabled}
                onSelect={() => setTimeout(() => onCompareVersions(), 100)}
                tooltipContent={compareDisabledTooltip}
            >
                <MenuItemWithIcon>
                    <IconSpacer />
                    {translate('pages.job-architecture-v2.job-profile-compare-versions')}
                </MenuItemWithIcon>
            </DropdownMenuItem>
            {!is.undefined(onCompareLevels) && (
                <DropdownMenuItem onSelect={onCompareLevels}>
                    <MenuItemWithIcon>
                        <IconTable size={16} />
                        {translate('pages.job-architecture-v2.job-profile-compare-levels')}
                    </MenuItemWithIcon>
                </DropdownMenuItem>
            )}
            {!is.undefined(onCompareRoles) && (
                <DropdownMenuItem onSelect={onCompareRoles}>
                    <MenuItemWithIcon>
                        <IconTable size={16} />
                        {translate('pages.job-architecture-v2.job-profile-compare-with')}
                    </MenuItemWithIcon>
                </DropdownMenuItem>
            )}
        </React.Fragment>
    );
};

type OptionsMenuProps = {
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly onArchive?: () => void;
    readonly onCompareLevels?: () => void;
    readonly onCompareRoles?: () => void;
    readonly onCompareVersions: (versionA: string, versionB: string, preferredLanguage?: LanguageCodeForJSA) => void;
    readonly onNewVersion: () => void;
    readonly onOpenSeparatePage?: () => void;
    readonly onRegenerate: () => void;
    readonly onUnreview?: () => Promise<void>;
    readonly onVersionChange: (value: string) => void;
    readonly selectedVersion: string | undefined;
};

export const OptionsMenu: FunctionComponent<OptionsMenuProps> = ({
    isEditing,
    item,
    onArchive,
    onCompareLevels,
    onCompareRoles,
    onCompareVersions,
    onNewVersion,
    onOpenSeparatePage,
    onRegenerate,
    onUnreview,
    onVersionChange,
    selectedVersion,
}) => {
    const translate = useTranslate();
    const { showErrorSnackbar } = useSnackbars();
    const { availableMethods, closeInviteDialog, handleInvite, isInviteDialogOpen, openInviteDialog } =
        useInviteTeamMember({
            architectureId: item.architectureId,
            itemId: item.id,
            itemType: item.type,
        });
    const { data: versions = [], isSuccess: isVersionsQuerySuccessful } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });
    const { formattedVersions, newTmpVersion } = useFormattedVersions(versions);

    const draftStore = useDraftVersionStore();
    const hasDraft = useStore(draftStore, (state) => state.drafts.has(item.id));
    const { activeLanguage } = useActiveLanguage();

    const handleCompareVersions = (): void => {
        if (!isValidVersionForCompare(selectedVersion)) {
            return;
        }

        const versionPair = getCompareVersionPair(selectedVersion, versions, hasDraft);
        if (is.nullOrUndefined(versionPair)) {
            return;
        }

        onCompareVersions(versionPair[0], versionPair[1], activeLanguage);
    };

    const currentVersionId = getCurrentVersionId(isEditing, newTmpVersion, formattedVersions, selectedVersion);
    const canEdit = item.actions?.canEdit === true;
    const canAssignReviewer = item.actions?.canAssignReviewer === true;
    const isEditingDisabled = isEditing || !canEdit;
    const isCompareDisabled = versions.length < 2 && !isEditing;
    const compareDisabledTooltip =
        isCompareDisabled && isVersionsQuerySuccessful
            ? translate('pages.job-architecture-v2.job-profile-compare-versions-disabled-tooltip')
            : undefined;
    const canRegenerate = !isEditing && canEdit;

    return (
        <React.Fragment>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button size="sm" variant="secondary">
                        {translate('common.options')}
                    </Button>
                </DropdownMenuTrigger>
                <StyledDropdownMenuContent align="end" sideOffset={5}>
                    <ActionMenuItems
                        compareDisabledTooltip={compareDisabledTooltip}
                        isCompareDisabled={isCompareDisabled}
                        isEditingDisabled={isEditingDisabled}
                        isRegenerateDisabled={!canRegenerate}
                        onCompareVersions={handleCompareVersions}
                        onCompareLevels={onCompareLevels}
                        onCompareRoles={onCompareRoles}
                        onNewVersion={onNewVersion}
                        onRegenerate={onRegenerate}
                    />
                    <ReviewerSubMenu canAssignReviewer={canAssignReviewer} item={item} onInvite={openInviteDialog} />
                    <DropdownMenuSeparator />
                    <VersionSubMenu
                        currentVersionId={currentVersionId}
                        formattedVersions={formattedVersions}
                        isEditing={isEditing}
                        onVersionChange={onVersionChange}
                    />
                    <ExportPdfMenuItem
                        item={item}
                        version={is.string(selectedVersion) ? Number.parseInt(selectedVersion, 10) : 1}
                    />
                    {!is.undefined(onOpenSeparatePage) && (
                        <OpenStandaloneMenuItem onOpenSeparatePage={onOpenSeparatePage} />
                    )}
                    <ShareJobMenuItem item={item} selectedVersion={selectedVersion} />
                    <WatchMenuItem itemId={item.id} />
                    <DropdownMenuSeparator />
                    {!is.undefined(onArchive) && (
                        <DropdownMenuItem disabled={!canEdit} onSelect={onArchive}>
                            <MenuItemWithIcon>
                                <IconArchive size={16} />
                                {translate('pages.job-architecture-v2.archive')}
                            </MenuItemWithIcon>
                        </DropdownMenuItem>
                    )}
                    {!is.undefined(onUnreview) && (
                        <DropdownMenuItem
                            disabled={!canEdit}
                            onSelect={() => void onUnreview().catch(showErrorSnackbar)}
                        >
                            <MenuItemWithIcon>
                                <IconSpacer />
                                {translate('common.enums.job-state.draft')}
                            </MenuItemWithIcon>
                        </DropdownMenuItem>
                    )}
                    <UnpublishOption item={item} />
                </StyledDropdownMenuContent>
            </DropdownMenu>
            <InviteTeamMemberDialog
                allowedRoles={JOB_ARCHITECTURE_INVITATION_ROLES}
                availableMethods={availableMethods}
                open={isInviteDialogOpen}
                title={translate('pages.company-settings.users-and-roles.invite-dialog.assign-review-title')}
                onClose={closeInviteDialog}
                onInvite={handleInvite}
            />
        </React.Fragment>
    );
};
